import { useCallback, useEffect, useRef, useState } from "react";
import {
  useVoiceInput,
  VoiceInputState,
} from "apps/user/learner/hooks/use-voice-input";
import { useVoiceAmplitude } from "apps/user/learner/hooks/use-voice-amplitude";
import { trackEvent } from "utility/mixpanel";

const PERMISSION_ERROR_CODES = new Set(["not-allowed", "service-not-allowed"]);

const errorMessageFor = (errorCode: string): string =>
  PERMISSION_ERROR_CODES.has(errorCode)
    ? "Microphone access was blocked. Enable it in your browser's site settings to talk to Ori."
    : "Voice input failed, please try again.";

interface UseOriVoiceOptions {
  onSend: (text: string) => void;
}

interface UseOriVoiceReturn {
  isSupported: boolean;
  state: VoiceInputState;
  hasTranscript: boolean;
  error: string | null;
  /** Live mic loudness (0–1) while listening; 0 when idle or amplitude is unavailable. */
  amplitude: number;
  /** True while an amplitude session is live, from mic startup onward. */
  capturingVoice: boolean;
  startListening: () => void;
  cancel: () => void;
  send: () => void;
}

// Voice-input orchestration for the composer: the deferred-send latch, the 1:1
// Started/Ended analytics pairing, and the end-of-session safety nets. Wraps
// useVoiceInput (recognizer lifecycle) so the composer only renders.
export function useOriVoice({ onSend }: UseOriVoiceOptions): UseOriVoiceReturn {
  const [hasTranscript, setHasTranscript] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `send()` calls `stop()`, but the corrected final transcript only arrives
  // (via `onFinal`) shortly after — this latch defers the dispatch until then.
  const pendingSendRef = useRef(false);
  // Timestamp of the outstanding Started; `null` = none, keeping Started/Ended paired 1:1.
  const startedAtRef = useRef<number | null>(null);

  const fireEnded = useCallback(
    (
      outcome: "sent" | "cancelled" | "failed",
      extras?: { transcriptLength?: number; errorCode?: string }
    ) => {
      if (startedAtRef.current === null) return;
      const durationMs = Date.now() - startedAtRef.current;
      startedAtRef.current = null;
      trackEvent("Coach Voice Input Ended", {
        Outcome: outcome,
        "Duration Ms": durationMs,
        ...(extras?.transcriptLength !== undefined && {
          "Transcript Length": extras.transcriptLength,
        }),
        ...(extras?.errorCode && { "Error Code": extras.errorCode }),
      });
    },
    []
  );

  const handleInterim = useCallback((interim: string) => {
    setHasTranscript(interim.trim().length > 0);
  }, []);

  const handleFinal = useCallback(
    (transcript: string) => {
      if (!pendingSendRef.current) return;
      pendingSendRef.current = false;
      onSend(transcript);
      fireEnded("sent", { transcriptLength: transcript.length });
      setHasTranscript(false);
    },
    [onSend, fireEnded]
  );

  const {
    isSupported,
    state,
    start,
    stop,
    abort,
  } = useVoiceInput({
    onInterim: handleInterim,
    onFinal: handleFinal,
    onStart: ({ locale }) => {
      startedAtRef.current = Date.now();
      trackEvent("Coach Voice Input Started", { Locale: locale });
    },
    onError: (errorCode) => {
      setError(errorMessageFor(errorCode));
      fireEnded("failed", { errorCode });
    },
  });

  // Gating on state ties the amplitude stream's teardown to the recognizer's.
  const { amplitude, capturing: capturingVoice } = useVoiceAmplitude({
    active: state === "listening",
  });

  // Session ended on silence (not send/cancel/error, which resolve themselves).
  useEffect(() => {
    if (state !== "idle") return;
    if (!pendingSendRef.current && startedAtRef.current === null) return;
    pendingSendRef.current = false;
    setHasTranscript(false);
    fireEnded("cancelled");
  }, [state, fireEnded]);

  // Don't orphan a Started if we unmount mid-listen.
  useEffect(
    () => () => {
      if (startedAtRef.current !== null) fireEnded("cancelled");
    },
    [fireEnded]
  );

  const startListening = useCallback(() => {
    setError(null);
    setHasTranscript(false);
    // An `error`-terminated session skips the idle effect, so clear its latch here.
    pendingSendRef.current = false;
    start();
  }, [start]);

  const cancel = useCallback(() => {
    abort();
    setHasTranscript(false);
    pendingSendRef.current = false;
    fireEnded("cancelled");
  }, [abort, fireEnded]);

  const send = useCallback(() => {
    if (pendingSendRef.current) return;
    pendingSendRef.current = true;
    stop();
  }, [stop]);

  return {
    isSupported,
    state,
    hasTranscript,
    error,
    amplitude,
    capturingVoice,
    startListening,
    cancel,
    send,
  };
}
